/* eslint-disable import/no-import-module-exports */
/**
 * THIS IS THE ENTRY POINT FOR THE CLIENT, JUST LIKE server.js IS THE ENTRY POINT FOR THE SERVER.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { loadableReady } from '@loadable/component';
// HistoryRouter lets us keep the auth middleware's history.push() architecture
// from v5 (react-router 6/7's BrowserRouter manages history internally).
// The export is marked unstable but has been around since v6 and is still the
// supported way to share a custom history instance with the router.
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import moment from 'moment';
// FormatJS v8+ subpath exports require the .js extension.
// eslint-disable-next-line import/extensions
import { shouldPolyfill } from '@formatjs/intl-listformat/should-polyfill.js';

import ChildRoutes from 'helpers/ChildRoutes';
import BedConfigContext from 'context/BedConfigContext';
import { domClassSanitizer, requestSanitizer, responseSanitizer } from 'logRocketConfig';
import ApiClient from './helpers/ApiClient';
import createStore from './state/create';
import constants from './constants';
import routes from './routes';

const LOGROCKET_ID = process.env.NODE_ENV === 'production' ? constants.LogRocketProd : constants.LogRocketDev;
/* eslint-disable-next-line */
import LogRocket from 'logrocket';
LogRocket.init(LOGROCKET_ID, {
  dom: {
    inputSanitizer: true,
    privateAttributeBlocklist: domClassSanitizer,
    privateClassNameBlocklist: domClassSanitizer,
  },
  network: {
    requestSanitizer,
    responseSanitizer,
  },
});

// Set up all the initial stuff
const localeCode = window.location.pathname.startsWith('/ja') ? 'ja' : 'en';

const initialState = window.__INITIAL_STATE__;
// eslint-disable-next-line no-underscore-dangle
const intlData = window.__LOCALE_DATA__ || { locale: localeCode, messages: {} };
const client = new ApiClient();
client.configure({ locale: localeCode });

// Update date display format for both locales
moment.updateLocale('en', constants.momentDateFormats.en);
moment.updateLocale('ja', constants.momentDateFormats.ja);
moment.locale(localeCode);

// Check if PluralRules is available, old browsers IE11, Chrome v.<=68 and Safari doesn't support it
if (!Intl.PluralRules) {
  // Enable the PluralRules polyfill
  // eslint-disable-next-line global-require, import/extensions
  require('@formatjs/intl-pluralrules/polyfill.js');
  // eslint-disable-next-line global-require, import/extensions
  require('@formatjs/intl-pluralrules/locale-data/en.js');
  // eslint-disable-next-line global-require, import/extensions
  require('@formatjs/intl-pluralrules/locale-data/ja.js');
}

const { store, history } = createStore({ initialState, client });
const root = createRoot(document.getElementById('content'));

function render(Routes) {
  root.render(
    <IntlProvider locale={intlData.locale} messages={intlData.messages} onError={() => {}}>
      <BedConfigContext value={intlData.bedConfigText || {}}>
        <Provider store={store}>
          <HistoryRouter history={history}>
            <ChildRoutes routes={Routes} />
          </HistoryRouter>
        </Provider>
      </BedConfigContext>
    </IntlProvider>
  );
}

// loadable-component setup
loadableReady(() => {
  render(routes);
});

// Polyfill the formatList API if the current user's browser doesn't support it
if (shouldPolyfill(localeCode)) {
  // eslint-disable-next-line global-require, import/extensions
  require('@formatjs/intl-listformat/polyfill-force.js');
  // Static requires so webpack can resolve through the package's exports field;
  // dynamic specifiers don't work against subpath-pattern exports.
  if (localeCode === 'ja') {
    // eslint-disable-next-line global-require, import/extensions
    require('@formatjs/intl-listformat/locale-data/ja.js');
  }
  else {
    // eslint-disable-next-line global-require, import/extensions
    require('@formatjs/intl-listformat/locale-data/en.js');
  }
}

if (module.hot) {
  // Enable webpack hot module replacement for routes
  module.hot.accept('./routes', () => {
    try {
      // eslint-disable-next-line global-require
      const nextRoutes = require('./routes').default;

      render(nextRoutes);
    }
    catch (error) {
      console.error(`==> ⚠  Routes hot reloading error ${error}`);
    }
  });
}
